export const CHAT_VOICE_MAX_MS = 120_000;
export const CHAT_VOICE_MIN_MS = 400;

const MIME_CANDIDATES: { mime: string; ext: string }[] = [
  { mime: "audio/webm;codecs=opus", ext: "webm" },
  { mime: "audio/webm", ext: "webm" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  { mime: "audio/ogg", ext: "ogg" },
];

export function chatVoiceSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function pickChatVoiceMime(): { mime: string; ext: string } {
  if (typeof MediaRecorder === "undefined") {
    return { mime: "", ext: "webm" };
  }
  for (const c of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(c.mime)) return c;
  }
  return { mime: "", ext: "webm" };
}

export async function startChatVoiceRecorder(): Promise<{
  recorder: MediaRecorder;
  stream: MediaStream;
  ext: string;
}> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: false,
  });
  const picked = pickChatVoiceMime();
  const recorder = picked.mime
    ? new MediaRecorder(stream, { mimeType: picked.mime })
    : new MediaRecorder(stream);
  const recMime = (recorder.mimeType || picked.mime).toLowerCase();
  const ext = recMime.includes("mp4") || recMime.includes("m4a") ? "m4a" : picked.ext;
  return { recorder, stream, ext };
}

export type ChatVoiceMeter = {
  readLevel: () => number;
  close: () => Promise<void>;
};

/** Niveau micro 0–1 pour l’onde live (sans renvoyer le son dans les HP). */
export function createChatVoiceMeter(stream: MediaStream): ChatVoiceMeter | null {
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.38;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    void ctx.resume();
    return {
      readLevel: () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        return Math.min(1, Math.sqrt(sum / buf.length) * 3.8);
      },
      close: async () => {
        try {
          source.disconnect();
        } catch {
          /* ignore */
        }
        try {
          await ctx.close();
        } catch {
          /* ignore */
        }
      },
    };
  } catch {
    return null;
  }
}

export function stopChatVoiceStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  }
}

export function blobToVoiceFile(blob: Blob, ext: string): File {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const type = blob.type || (ext === "m4a" ? "audio/mp4" : `audio/${ext}`);
  return new File([blob], `vocal-${stamp}.${ext}`, { type });
}

export function formatVoiceClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const CHAT_VOICE_WAVE_BARS = 40;

/** Plus de barres sur grand écran pour éviter l’effet « élastique ». */
export function chatVoiceLiveBarCount(cssWidth: number): number {
  const w = Math.max(1, cssWidth);
  const desktop =
    typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
  if (!desktop) {
    return CHAT_VOICE_WAVE_BARS;
  }
  return Math.max(56, Math.min(160, Math.round(w / 2.7)));
}

export function chatVoicePauseSupported(): boolean {
  return typeof MediaRecorder !== "undefined" && typeof MediaRecorder.prototype.pause === "function";
}

function fallbackVoicePeaks(seed: string, bars: number): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const out: number[] = [];
  for (let i = 0; i < bars; i++) {
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    out.push(0.16 + ((h >>> 8) % 78) / 100);
  }
  return out;
}

const peaksCache = new Map<string, Promise<number[]>>();

/** Pics 0–1 depuis le fichier audio (cache par URL). */
export function loadVoicePeaks(url: string, bars = CHAT_VOICE_WAVE_BARS): Promise<number[]> {
  const cached = peaksCache.get(url);
  if (cached) return cached;
  const job = (async () => {
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return fallbackVoicePeaks(url, bars);
      const raw = await res.arrayBuffer();
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return fallbackVoicePeaks(url, bars);
      const ctx = new Ctor();
      const audio = await ctx.decodeAudioData(raw.slice(0));
      void ctx.close();
      const data = audio.getChannelData(0);
      if (!data.length) return fallbackVoicePeaks(url, bars);
      const block = Math.max(1, Math.floor(data.length / bars));
      const peaks: number[] = [];
      for (let i = 0; i < bars; i++) {
        let max = 0;
        const start = i * block;
        const end = Math.min(data.length, start + block);
        for (let j = start; j < end; j += 4) {
          const v = Math.abs(data[j] ?? 0);
          if (v > max) max = v;
        }
        peaks.push(max);
      }
      const peak = Math.max(...peaks, 0.04);
      return peaks.map((p) => 0.12 + (p / peak) * 0.88);
    } catch {
      return fallbackVoicePeaks(url, bars);
    }
  })();
  peaksCache.set(url, job);
  return job;
}

function resolveCanvasColor(canvas: HTMLCanvasElement, raw: string | undefined, fallback: string): string {
  const t = (raw || "").trim();
  if (!t || t === "currentColor") {
    return getComputedStyle(canvas).color || fallback;
  }
  return t;
}

function paintVoiceBars(
  ctx: CanvasRenderingContext2D,
  levels: number[],
  w: number,
  h: number,
  dpr: number,
  color: string
): void {
  const gap = Math.max(1.2 * dpr, w * 0.012);
  const barW = Math.max(1.6 * dpr, (w - gap * (levels.length - 1)) / levels.length);
  const mid = h / 2;
  ctx.fillStyle = color;
  for (let i = 0; i < levels.length; i++) {
    const amp = Math.max(0.08, Math.min(1, levels[i] ?? 0.08));
    const barH = Math.max(2 * dpr, amp * h * 0.92);
    const x = i * (barW + gap);
    const y = mid - barH / 2;
    const r = Math.min(barW / 2, 1.6 * dpr);
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(x, y, barW, barH, r);
    } else {
      ctx.rect(x, y, barW, barH);
    }
    ctx.fill();
  }
}

export function drawVoiceWaveform(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  levels: number[],
  opts?: { progress?: number; played?: string; rest?: string }
): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || 1;
  const cssH = canvas.clientHeight || 1;
  const w = Math.round(cssW * dpr);
  const h = Math.round(cssH * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  ctx.clearRect(0, 0, w, h);
  const played = resolveCanvasColor(canvas, opts?.played, "#e8edf5");
  const rest = resolveCanvasColor(canvas, opts?.rest, played);
  const progress = Math.min(1, Math.max(0, opts?.progress ?? 1));
  paintVoiceBars(ctx, levels, w, h, dpr, rest);
  if (progress >= 1) {
    paintVoiceBars(ctx, levels, w, h, dpr, played);
    return;
  }
  if (progress <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w * progress, h);
  ctx.clip();
  paintVoiceBars(ctx, levels, w, h, dpr, played);
  ctx.restore();
}
